import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AdminRole = "super_admin" | "support_agent" | "billing_admin" | "read_only";

export interface AdminUser {
  id: string;
  user_id: string | null;
  email: string;
  role: string;
  status: string;
  created_at: string;
  last_login: string | null;
  invited_by: string | null;
}

export const useAdminUsers = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_users")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as AdminUser[];
    },
    enabled: !!user,
  });
};

export const useAddAdminUser = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payload: { email: string; role: AdminRole }) => {
      const { data, error } = await supabase
        .from("admin_users")
        .insert({
          email: payload.email,
          role: payload.role,
          status: "active",
          invited_by: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};

export const useUpdateAdminUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; role?: string; status?: string }) => {
      const { id, ...updates } = payload;
      const { data, error } = await supabase
        .from("admin_users")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
};
